<script lang="ts">
  import {onMount} from 'svelte';
  import {deal} from 'Store/InvestorFlowStore';
  import {incentivePlans} from 'Store/deals/incentivePlan';
  import {dollarMask} from 'Utility/formatters/mask';
  import MaskedInput from 'Catalyst/components/MaskedInput/MaskedInput.svelte';
  import {integerMaskOption} from 'Utility/formatters/mask';
  import CatalystStyleOverwrite from 'Components/InvestorFlow/CatalystStyleOverwrite.svelte';

  export let customInvestmentAmount: number = $deal.minimum_investment; // In cents
  export let numSecurities: number;
  export let price_per_security: number; // In cents
  export let maskedValue: string; // In dollars for display purpose

  const updateInvestmentAmount = (): void => {
    const updatedAmount = Number(numSecuritiesString) * price_per_security;
    maskedValue = String(Math.ceil(updatedAmount) / 100);
    customInvestmentAmount = updatedAmount;
  };

  const handleOnChange = (): void => {
    if (maskedValue) {
      // We need to wait the previous priority event in order to update the amount. Tick does not work here.
      setTimeout(updateInvestmentAmount);
    }
    numSecurities = Number(numSecuritiesString);
  };

  $: originalInvestmentAmount =
    numSecurities * $incentivePlans?.full_price?.amount;

  $: discountedInvestmentAmount =
    numSecurities * $incentivePlans?.final_price?.amount;
  $: hasIncentive =
    !!$incentivePlans?.effective_tier ||
    discountedInvestmentAmount < originalInvestmentAmount;

  onMount(() => {
    updateInvestmentAmount();
  });

  $: numSecuritiesString = String(numSecurities);

  const mappings = {
    '--base-input-slots-base-bg-color': '--dm-bg-secondary-color',
    '--base-input-variants-state-hover-base-bg-color':
      '--dm-bg-secondary-color',
    '--base-input-variants-state-active-base-bg-color':
      '--dm-bg-secondary-color',
    '--base-input-slots-input-value-color': '--dm-text-primary-color',
  };
</script>

<div class="flex flex-col items-center">
  <CatalystStyleOverwrite {mappings}>
    <MaskedInput
      iMaskOptions={integerMaskOption}
      showPlaceholder={true}
      placeholder="Shares"
      bind:value={numSecuritiesString}
      on:change={handleOnChange}
      unmask
      class="p-0"
      inputTestId="input-money-container"
    />
  </CatalystStyleOverwrite>

  <p class="custom-investment-subtext" data-testid="custom-investment-subtext">
    <span data-testid="security-type" class="security-type">
      {$deal.security_type}
    </span>
    <span data-testid="number-of-securities" class="num-securities">
      <span>= </span>
      {#if hasIncentive}
        <s>
          {dollarMask(
            originalInvestmentAmount.toFixed(2),
            $deal.currency_symbol
          )}</s
        >
      {/if}
      <span>
        {dollarMask(maskedValue, $deal.currency_symbol)}
      </span>
    </span>
  </p>
</div>

<style>
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input:focus {
    outline: none;
    cursor: text;
  }

  .custom-investment-subtext {
    white-space: normal;
    font-size: 1rem;
    margin-bottom: 0;
    margin-top: 0.5rem;
    text-align: center;
  }

  .money-container {
    box-sizing: border-box;
    padding: 0.625rem 0.875rem;
    gap: 0.5rem;
    background: var(--dm-bg-primary-color, var(--palettes-neutral-0, #ffffff));
    border: 1px solid
      var(--dm-input-border-color, var(--palettes-neutral-150, #d0d5dd));
    box-shadow: 0px 1px 2px rgba(16, 24, 40, 0.05);
    border-radius: 4px;
    flex: none;
    order: 1;
    align-self: stretch;
    flex-grow: 0;
    text-align: center;
    width: 16rem;
  }

  .num-securities {
    display: block;
    font-weight: 500;
    font-size: 1rem;
    line-height: 1.5rem;
    text-align: left;
    color: var(--dm-text-secondary-color, var(--palettes-neutral-575, #667085));
  }

  .security-type {
    display: flex;
    justify-content: left;
    font-weight: bold;
    font-size: 0.9375rem;
    color: var(--dm-text-secondary-color, var(--palettes-neutral-575, #667085));
  }
</style>
